import { OmniScrollArea } from "@/components/artifacts/OmniScrollArea.tsx";
import { iconForName } from "@/components/artifacts/iconForName.ts";
import { sanitizeRichTextHtml } from "@/lib/sanitizeHtml.ts";
import type {
  ArtifactSummary,
  ArtifactSummaryExcerpt,
  ArtifactSummaryField,
} from "@/types/artifacts.ts";
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";

/**
 * The preview card inside a biscuit — optional icon+subtitle header, labeled
 * rows, body excerpt. Pure render: every variant comes from the summary it is
 * handed, no artifact-type-specific branching.
 *
 * A heavy artifact's summary is server-emitted (`artifact.biscuit`);
 * `OmniArtifactFormBiscuit` builds one client-side from a form's questions,
 * since a light `userResponse` carries no server biscuit. Its own module rather
 * than a `ArtifactBiscuit` export so the form card doesn't pull the heavy
 * biscuit's render graph (ArtifactView and the editors behind it) along with it.
 */
export const SummaryCard = ({ summary }: { summary: ArtifactSummary }) => {
  const SubtitleIcon = summary.icon ? iconForName(summary.icon) : null;
  return (
    // Header, rows and body share one scroll view: they are one preview, and
    // scrolling them apart left the header pinned over a list that had already
    // moved on.
    //
    // The box's padding lives here rather than on a parent so the bar belongs to
    // the scroller instead of floating in a gutter beside it. Clearance from the
    // 10px corner radius, which would otherwise mask the thumb's rounded tips at
    // the travel extremes, is the scroll area's own `--omni-thumb-inset`.
    <OmniScrollArea className="max-h-[75px] md:max-h-[209px] px-4 py-3 flex flex-col gap-2">
      {summary.subtitle && (
        <div className="flex items-center gap-2 text-xs font-semibold leading-snug">
          {SubtitleIcon && (
            <SubtitleIcon className="size-4 text-muted-foreground shrink-0" aria-hidden />
          )}
          <span className="truncate">{summary.subtitle}</span>
        </div>
      )}
      {summary.fields && summary.fields.length > 0 && (
        <div className="flex flex-col gap-0.5 text-xs">
          {summary.fields.map((field, i) => (
            <SummaryFieldRow key={i} field={field} />
          ))}
        </div>
      )}
      {summary.excerpt && <SummaryExcerpt excerpt={summary.excerpt} />}
    </OmniScrollArea>
  );
};

const SummaryFieldRow = ({ field }: { field: ArtifactSummaryField }) => {
  switch (field.type) {
    // A server-emitted row pairs a short key ("Subject:") with a long value, but
    // `OmniArtifactFormBiscuit` puts a whole question in the label — and an
    // unshrinkable label sits at its max-content width, so a long one ran the row
    // off the side of the biscuit and shoved the answer out of sight (BIG-7470).
    // The cap makes a long label wrap down the rows instead, and reserves the
    // room the answer needs to stay on screen beside it. A short key is narrower
    // than the cap, so it's untouched.
    case "value":
      return (
        <div className="flex flex-row items-baseline gap-2 min-w-0">
          {field.label && (
            <span className="text-muted-foreground shrink-0 max-w-[80%]">{field.label}</span>
          )}
          <span className="text-foreground font-medium truncate">{field.value}</span>
        </div>
      );
    case "diff":
      return (
        <div className="flex flex-row items-baseline gap-1.5 flex-wrap">
          {field.label && <span className="text-muted-foreground shrink-0">{field.label}</span>}
          <span className="text-muted-foreground line-through">{field.before}</span>
          <ArrowRight className="size-3 text-muted-foreground shrink-0" aria-hidden />
          <span className="text-foreground font-medium">{field.after}</span>
        </div>
      );
    case "list":
      return (
        <div className="flex flex-col gap-0.5">
          {field.label && <span className="text-muted-foreground">{field.label}</span>}
          {field.items.map((item, i) => (
            <div key={i} className="text-muted-foreground truncate text-xs">
              · {item}
            </div>
          ))}
          {field.moreCount && field.moreCount > 0 && (
            <div className="text-muted-foreground text-xs">+ {field.moreCount} more</div>
          )}
        </div>
      );
  }
};

/** The body preview. Height is the card's to cap; this just renders. */
const SummaryExcerpt = ({ excerpt }: { excerpt: ArtifactSummaryExcerpt }) => {
  const sanitized = useMemo(() => {
    if (excerpt.format !== "html") return null;
    return sanitizeRichTextHtml(excerpt.value);
  }, [excerpt.format, excerpt.value]);

  if (excerpt.format === "html") {
    if (!sanitized) return null;
    return (
      <div
        className="text-xs text-foreground leading-relaxed [&_p]:mb-1 [&_p:last-child]:mb-0 [&_a]:underline"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: sanitized with DOMPurify (tight allowlist)
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return (
    <div className="text-xs text-foreground leading-relaxed whitespace-pre-wrap">
      {excerpt.value}
    </div>
  );
};
